// ============================================================
// CREATE RESTAURANT — 13-Step Admin Onboarding Wizard
// Route: /admin/restaurants/create
// Allows full restaurant onboarding: info → owner → address →
// compliance → banking → hours → slots → kitchen config →
// delivery → payments → menu → staff → publish
// ============================================================

import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, Upload, Plus, Trash2,
  Building2, User, MapPin, FileText, CreditCard, Clock,
  CalendarDays, Settings, Truck, Wallet, UtensilsCrossed,
  Users, Eye, ChevronDown, ChevronUp, AlertCircle, X,
  RefreshCw,
} from 'lucide-react';
import { adminApi } from '../services/apiService';
import { toast } from 'react-toastify';

// ─────────────────────────────────────────────────────────────
// WIZARD STEP DEFINITIONS
// ─────────────────────────────────────────────────────────────
const STEPS = [
  { id: 1,  title: 'Restaurant Info',    subtitle: 'Name, branding & description',   icon: Building2 },
  { id: 2,  title: 'Owner Info',         subtitle: 'Owner details & account setup',  icon: User },
  { id: 3,  title: 'Business Address',   subtitle: 'Location & service area',        icon: MapPin },
  { id: 4,  title: 'Compliance Docs',    subtitle: 'Licenses, permits & insurance',  icon: FileText },
  { id: 5,  title: 'Banking & Payout',   subtitle: 'Bank account & payout schedule', icon: Wallet },
  { id: 6,  title: 'Operating Hours',    subtitle: 'Weekly schedule & holidays',     icon: Clock },
  { id: 7,  title: 'Order Slots',        subtitle: 'Pickup time configuration',      icon: CalendarDays },
  { id: 8,  title: 'Kitchen Config',     subtitle: 'Orders & capacity settings',     icon: Settings },
  { id: 9,  title: 'Delivery Config',    subtitle: 'Service modes & delivery rules', icon: Truck },
  { id: 10, title: 'Payment Config',     subtitle: 'Accepted methods & processors',  icon: CreditCard },
  { id: 11, title: 'Menu Setup',         subtitle: 'Categories & initial menu',      icon: UtensilsCrossed },
  { id: 12, title: 'Staff Accounts',     subtitle: 'Create kitchen staff logins',    icon: Users },
  { id: 13, title: 'Review & Publish',   subtitle: 'Final settings & go live',       icon: Eye },
];

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
  'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
  'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
  'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
  'Wisconsin','Wyoming',
];

const CUISINE_OPTIONS = [
  'American','Biryani','Chinese','Continental','Desserts','Indian',
  'Italian','Japanese','Mediterranean','Mexican','Middle Eastern',
  'Pizza','Seafood','South Indian','Thai','Vegan','Vietnamese',
];

const STAFF_ROLES = [
  { value: 'super_admin',       label: 'Restaurant Owner' },
  { value: 'admin',             label: 'Manager' },
  { value: 'kitchen_manager',   label: 'Kitchen Staff' },
  { value: 'finance_manager',   label: 'Dispatcher' },
  { value: 'support_executive', label: 'Customer Support' },
];

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];

// ─────────────────────────────────────────────────────────────
// INITIAL FORM STATE
// ─────────────────────────────────────────────────────────────
const initForm = () => ({
  restaurant_info: {
    restaurant_name: '', display_name: '', description: '',
    cuisine_types: [], restaurant_category: 'restaurant',
    business_type: 'llc', year_established: '',
  },
  owner_info: {
    first_name: '', last_name: '', email: '', mobile: '',
    username: '', password: '',
    send_welcome_email: true, send_password_link: false,
  },
  address_info: {
    street: '', suite: '', city: '', state: '', zip_code: '',
    country: 'USA', latitude: '', longitude: '', service_radius: '10',
  },
  compliance: {
    business_license:    { number: '', expiry: '' },
    ein:                 { number: '' },
    food_service_permit: { number: '', expiry: '' },
    health_permit:       { number: '', expiry: '' },
    sales_tax_permit:    {},
    insurance:           { provider: '', expiry: '' },
    state_registration:  {},
    reseller_permit:     {},
    fire_inspection:     {},
    occupancy_permit:    {},
  },
  banking: {
    account_holder: '', bank_name: '', routing_number: '',
    account_number: '', payout_frequency: 'weekly',
    tax_classification: 'llc',
  },
  operating_hours: {
    monday:    { open: '09:00', close: '21:00', closed: false },
    tuesday:   { open: '09:00', close: '21:00', closed: false },
    wednesday: { open: '09:00', close: '21:00', closed: false },
    thursday:  { open: '09:00', close: '21:00', closed: false },
    friday:    { open: '09:00', close: '22:00', closed: false },
    saturday:  { open: '10:00', close: '22:00', closed: false },
    sunday:    { open: '11:00', close: '20:00', closed: false },
  },
  slot_config: {
    slot_duration: 60, prep_time: 20,
    generated_slots: [], custom_slots: [],
  },
  kitchen_config: {
    auto_accept: false, default_prep_time: 20, rush_prep_time: 35,
    max_orders_per_hour: 20, max_orders_per_slot: 5,
  },
  delivery_config: {
    delivery_enabled: true, pickup_enabled: true, catering_enabled: false,
    min_order_amount: 15, delivery_radius: 10,
    estimated_delivery_time: 45, delivery_fee: 3.99,
    free_delivery_threshold: 50,
  },
  payment_config: {
    credit_cards: true, debit_cards: true,
    apple_pay: true, google_pay: true, cash_pickup: false,
    stripe_account: '', platform_account: '',
  },
  menu_config: {
    categories: [
      { name: 'Appetizers', description: '' },
      { name: 'Biryani', description: '' },
      { name: 'Entrees', description: '' },
      { name: 'Desserts', description: '' },
      { name: 'Drinks', description: '' },
    ],
  },
  staff: [],
  visibility: {
    visible_on_app: true, visible_on_website: true,
    featured: false, recommended: false,
  },
  status_label: 'draft',
  branding: { logo_url: '', cover_url: '', banner_url: '', gallery: [] },
});

const initFiles = () => ({
  logo: null, cover: null, banner: null, gallery: [],
  business_license: null, state_registration: null,
  food_permit: null, health_permit: null, sales_tax_permit: null,
  insurance_cert: null, w9: null,
  reseller_permit: null, fire_inspection: null, occupancy_permit: null,
});

// ─────────────────────────────────────────────────────────────
// SHARED FORM COMPONENTS
// ─────────────────────────────────────────────────────────────
const Label = ({ children, required }) => (
  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--admin-text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
    {children} {required && <span style={{ color: '#EF4444' }}>*</span>}
  </label>
);

const Input = ({ label, required, error, hint, ...props }) => (
  <div style={{ marginBottom: 18 }}>
    {label && <Label required={required}>{label}</Label>}
    <input
      {...props}
      style={{
        width: '100%', padding: '10px 12px', border: `1px solid ${error ? '#EF4444' : 'var(--admin-border)'}`,
        borderRadius: 8, background: 'var(--admin-card)', color: 'var(--admin-text)',
        fontSize: 14, outline: 'none', boxSizing: 'border-box',
        transition: 'border-color 0.15s',
        ...(props.style || {}),
      }}
    />
    {error && <div style={{ color: '#EF4444', fontSize: 12, marginTop: 4 }}>{error}</div>}
    {hint && !error && <div style={{ color: 'var(--admin-text-muted)', fontSize: 12, marginTop: 4 }}>{hint}</div>}
  </div>
);

const Select = ({ label, required, error, options, placeholder, ...props }) => (
  <div style={{ marginBottom: 18 }}>
    {label && <Label required={required}>{label}</Label>}
    <select
      {...props}
      style={{
        width: '100%', padding: '10px 12px', border: `1px solid ${error ? '#EF4444' : 'var(--admin-border)'}`,
        borderRadius: 8, background: 'var(--admin-card)', color: 'var(--admin-text)',
        fontSize: 14, outline: 'none', boxSizing: 'border-box',
        ...(props.style || {}),
      }}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => (
        <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
      ))}
    </select>
    {error && <div style={{ color: '#EF4444', fontSize: 12, marginTop: 4 }}>{error}</div>}
  </div>
);

const Textarea = ({ label, required, ...props }) => (
  <div style={{ marginBottom: 18 }}>
    {label && <Label required={required}>{label}</Label>}
    <textarea
      {...props}
      rows={props.rows || 3}
      style={{
        width: '100%', padding: '10px 12px', border: '1px solid var(--admin-border)',
        borderRadius: 8, background: 'var(--admin-card)', color: 'var(--admin-text)',
        fontSize: 14, outline: 'none', boxSizing: 'border-box', resize: 'vertical',
        ...(props.style || {}),
      }}
    />
  </div>
);

const Toggle = ({ label, description, checked, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--admin-border)' }}>
    <div>
      <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
      {description && <div style={{ fontSize: 12, color: 'var(--admin-text-muted)', marginTop: 2 }}>{description}</div>}
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      style={{
        width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
        background: checked ? '#E85D04' : 'var(--admin-border)', position: 'relative',
        transition: 'background 0.2s', flexShrink: 0,
      }}
    >
      <span style={{
        position: 'absolute', top: 3, left: checked ? 22 : 3,
        width: 18, height: 18, borderRadius: '50%', background: '#fff',
        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </button>
  </div>
);

const FileUploadBox = ({ label, accept = '.pdf,.jpg,.jpeg,.png', file, onChange, hint }) => {
  const ref = useRef();
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <Label>{label}</Label>}
      <div
        onClick={() => ref.current?.click()}
        style={{
          border: '2px dashed var(--admin-border)', borderRadius: 8, padding: '20px 16px',
          textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.2s',
          background: file ? '#F0FDF4' : 'var(--admin-bg)',
        }}
      >
        <input ref={ref} type="file" accept={accept} style={{ display: 'none' }}
          onChange={e => onChange(e.target.files[0] || null)} />
        {file ? (
          <div style={{ color: '#10B981', fontSize: 13 }}>
            <Check size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            {file.name}
          </div>
        ) : (
          <>
            <Upload size={20} style={{ color: 'var(--admin-text-muted)', marginBottom: 6 }} />
            <div style={{ fontSize: 13, color: 'var(--admin-text-muted)' }}>Click to upload</div>
          </>
        )}
      </div>
      {hint && <div style={{ fontSize: 11, color: 'var(--admin-text-muted)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <h3 style={{ fontSize: 14, fontWeight: 800, color: '#E85D04', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 18, marginTop: 28, paddingBottom: 8, borderBottom: '2px solid #FFF7ED' }}>
    {children}
  </h3>
);

const Grid2 = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>{children}</div>
);

// ─────────────────────────────────────────────────────────────
// STEP 1 — Restaurant Information
// ─────────────────────────────────────────────────────────────
const Step1 = ({ form, update, files, setFiles, errors }) => {
  const ri = form.restaurant_info;
  const set = (f, v) => update('restaurant_info', f, v);

  const toggleCuisine = (c) => {
    const list = ri.cuisine_types || [];
    set('cuisine_types', list.includes(c) ? list.filter(x => x !== c) : [...list, c]);
  };

  return (
    <div>
      <SectionTitle>Restaurant Details</SectionTitle>
      <Grid2>
        <Input label="Restaurant Name" required value={ri.restaurant_name}
          onChange={e => set('restaurant_name', e.target.value)}
          placeholder="e.g. Spice Garden" error={errors.restaurant_name} />
        <Input label="Display Name" value={ri.display_name}
          onChange={e => set('display_name', e.target.value)}
          placeholder="Short name shown to customers" />
      </Grid2>
      <Textarea label="Business Description" value={ri.description}
        onChange={e => set('description', e.target.value)}
        placeholder="Tell customers what makes your restaurant special..." rows={3} />
      <Grid2>
        <Select label="Restaurant Category" value={ri.restaurant_category}
          onChange={e => set('restaurant_category', e.target.value)}
          options={[
            { value: 'restaurant', label: 'Restaurant' },
            { value: 'cloud_kitchen', label: 'Cloud Kitchen' },
            { value: 'catering', label: 'Catering' },
            { value: 'food_truck', label: 'Food Truck' },
            { value: 'bakery', label: 'Bakery' },
            { value: 'cafe', label: 'Café' },
          ]} />
        <Select label="Business Type" value={ri.business_type}
          onChange={e => set('business_type', e.target.value)}
          options={[
            { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
            { value: 'llc', label: 'LLC' },
            { value: 'corporation', label: 'Corporation' },
            { value: 'partnership', label: 'Partnership' },
          ]} />
      </Grid2>
      <Input label="Year Established" value={ri.year_established}
        onChange={e => set('year_established', e.target.value)}
        placeholder="e.g. 2018" style={{ maxWidth: 200 }} type="number" min="1900" max="2026" />

      <div style={{ marginBottom: 18 }}>
        <Label>Cuisine Types</Label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {CUISINE_OPTIONS.map(c => {
            const sel = (ri.cuisine_types || []).includes(c);
            return (
              <button key={c} type="button" onClick={() => toggleCuisine(c)}
                style={{
                  padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', border: `2px solid ${sel ? '#E85D04' : 'var(--admin-border)'}`,
                  background: sel ? '#FFF7ED' : 'transparent',
                  color: sel ? '#E85D04' : 'var(--admin-text-secondary)',
                  transition: 'all 0.15s',
                }}
              >{c}</button>
            );
          })}
        </div>
      </div>

      <SectionTitle>Branding</SectionTitle>
      <Grid2>
        <FileUploadBox label="Restaurant Logo" accept=".jpg,.jpeg,.png,.webp"
          file={files.logo} onChange={f => setFiles(p => ({ ...p, logo: f }))}
          hint="Square image, min 200×200px" />
        <FileUploadBox label="Cover Image" accept=".jpg,.jpeg,.png,.webp"
          file={files.cover} onChange={f => setFiles(p => ({ ...p, cover: f }))}
          hint="16:9 ratio recommended, min 1200×675px" />
      </Grid2>
      <Grid2>
        <FileUploadBox label="Promotional Banner" accept=".jpg,.jpeg,.png,.webp"
          file={files.banner} onChange={f => setFiles(p => ({ ...p, banner: f }))}
          hint="Banner displayed on app home screen" />
        <FileUploadBox label="Gallery Images" accept=".jpg,.jpeg,.png,.webp"
          file={files.gallery[0]} onChange={f => setFiles(p => ({ ...p, gallery: f ? [f] : [] }))}
          hint="Multiple images of your food & space" />
      </Grid2>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// STEP 2 — Owner Information
// ─────────────────────────────────────────────────────────────
const Step2 = ({ form, update, errors }) => {
  const oi = form.owner_info;
  const set = (f, v) => update('owner_info', f, v);
  return (
    <div>
      <SectionTitle>Primary Owner</SectionTitle>
      <Grid2>
        <Input label="First Name" required value={oi.first_name}
          onChange={e => set('first_name', e.target.value)}
          error={errors.first_name} />
        <Input label="Last Name" required value={oi.last_name}
          onChange={e => set('last_name', e.target.value)}
          error={errors.last_name} />
      </Grid2>
      <Grid2>
        <Input label="Email Address" required type="email" value={oi.email}
          onChange={e => set('email', e.target.value)}
          error={errors.email} />
        <Input label="Mobile Number" required type="tel" value={oi.mobile}
          onChange={e => set('mobile', e.target.value)}
          placeholder="+1 (555) 000-0000" error={errors.mobile} />
      </Grid2>

      <SectionTitle>Account Creation</SectionTitle>
      <Grid2>
        <Input label="Username" required value={oi.username}
          onChange={e => set('username', e.target.value)}
          placeholder="Unique login username" error={errors.username}
          hint="Owner will use this to log in to the kitchen portal" />
        <Input label="Temporary Password" required type="password" value={oi.password}
          onChange={e => set('password', e.target.value)}
          error={errors.password}
          hint="Min 8 characters. Owner should change on first login." />
      </Grid2>
      <div style={{ padding: '4px 0' }}>
        <Toggle label="Send Welcome Email" description="Email owner with login instructions"
          checked={oi.send_welcome_email} onChange={v => set('send_welcome_email', v)} />
        <Toggle label="Send Password Setup Link" description="Send a secure link to set their own password"
          checked={oi.send_password_link} onChange={v => set('send_password_link', v)} />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// STEP 3 — Business Address
// ─────────────────────────────────────────────────────────────
const Step3 = ({ form, update, errors }) => {
  const ai = form.address_info;
  const set = (f, v) => update('address_info', f, v);
  return (
    <div>
      <SectionTitle>Physical Location</SectionTitle>
      <Input label="Street Address" required value={ai.street}
        onChange={e => set('street', e.target.value)}
        placeholder="123 Main Street" error={errors.street} />
      <Grid2>
        <Input label="Suite / Unit Number" value={ai.suite}
          onChange={e => set('suite', e.target.value)}
          placeholder="Suite 201 (optional)" />
        <Input label="Country" value={ai.country}
          onChange={e => set('country', e.target.value)} />
      </Grid2>
      <Grid2>
        <Input label="City" required value={ai.city}
          onChange={e => set('city', e.target.value)}
          error={errors.city} />
        <Select label="State" required value={ai.state}
          onChange={e => set('state', e.target.value)}
          options={US_STATES} placeholder="Select state"
          error={errors.state} />
      </Grid2>
      <Grid2>
        <Input label="ZIP Code" required value={ai.zip_code}
          onChange={e => set('zip_code', e.target.value)}
          placeholder="10001" error={errors.zip_code} />
        <Input label="Service Radius (miles)" type="number" value={ai.service_radius}
          onChange={e => set('service_radius', e.target.value)} min="1" max="100" />
      </Grid2>

      <SectionTitle>Location Services</SectionTitle>
      <div style={{ padding: '12px 16px', background: 'var(--admin-bg)', borderRadius: 8, marginBottom: 16, fontSize: 13, color: 'var(--admin-text-muted)' }}>
        💡 Paste the GPS coordinates from Google Maps for precise delivery radius calculations.
      </div>
      <Grid2>
        <Input label="Latitude" value={ai.latitude}
          onChange={e => set('latitude', e.target.value)}
          placeholder="40.7128" type="number" step="any" />
        <Input label="Longitude" value={ai.longitude}
          onChange={e => set('longitude', e.target.value)}
          placeholder="-74.0060" type="number" step="any" />
      </Grid2>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// STEP 4 — Compliance Documents
// ─────────────────────────────────────────────────────────────
const DocSection = ({ title, required, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ border: '1px solid var(--admin-border)', borderRadius: 10, marginBottom: 16, overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', cursor: 'pointer', background: 'var(--admin-bg)' }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>
          {title}
          {required && <span style={{ color: '#EF4444', marginLeft: 6, fontSize: 11 }}>Required</span>}
        </div>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </div>
      {open && <div style={{ padding: '16px' }}>{children}</div>}
    </div>
  );
};

const Step4 = ({ form, update, files, setFiles, errors }) => {
  const c = form.compliance;
  const setC = (doc, field, val) => update('compliance', doc, { ...c[doc], [field]: val });
  return (
    <div>
      <SectionTitle>Required Business Documents</SectionTitle>

      <DocSection title="Business License" required>
        <Grid2>
          <Input label="License Number" required value={c.business_license.number}
            onChange={e => setC('business_license', 'number', e.target.value)}
            error={errors.business_license_number} />
          <Input label="Expiration Date" required type="date" value={c.business_license.expiry}
            onChange={e => setC('business_license', 'expiry', e.target.value)}
            error={errors.business_license_expiry} />
        </Grid2>
        <FileUploadBox label="Upload Business License" file={files.business_license}
          onChange={f => setFiles(p => ({ ...p, business_license: f }))} hint="PDF, JPG, or PNG" />
      </DocSection>

      <DocSection title="Employer Identification Number (EIN)" required>
        <Input label="EIN Number" required value={c.ein.number}
          onChange={e => setC('ein', 'number', e.target.value)}
          placeholder="XX-XXXXXXX" error={errors.ein_number}
          hint="9-digit federal tax ID (format: XX-XXXXXXX)" style={{ maxWidth: 260 }} />
      </DocSection>

      <DocSection title="State Business Registration" required>
        <FileUploadBox label="Upload Registration Document" file={files.state_registration}
          onChange={f => setFiles(p => ({ ...p, state_registration: f }))} hint="State-issued certificate of formation" />
      </DocSection>

      <DocSection title="Food Service Permit" required>
        <Grid2>
          <Input label="Permit Number" required value={c.food_service_permit.number}
            onChange={e => setC('food_service_permit', 'number', e.target.value)}
            error={errors.food_permit_number} />
          <Input label="Expiration Date" required type="date" value={c.food_service_permit.expiry}
            onChange={e => setC('food_service_permit', 'expiry', e.target.value)}
            error={errors.food_permit_expiry} />
        </Grid2>
        <FileUploadBox label="Upload Food Service Permit" file={files.food_permit}
          onChange={f => setFiles(p => ({ ...p, food_permit: f }))} />
      </DocSection>

      <DocSection title="Health Department Permit" required>
        <Grid2>
          <Input label="Permit Number" required value={c.health_permit.number}
            onChange={e => setC('health_permit', 'number', e.target.value)} />
          <Input label="Expiration Date" required type="date" value={c.health_permit.expiry}
            onChange={e => setC('health_permit', 'expiry', e.target.value)} />
        </Grid2>
        <FileUploadBox label="Upload Health Department Permit" file={files.health_permit}
          onChange={f => setFiles(p => ({ ...p, health_permit: f }))} />
      </DocSection>

      <DocSection title="Sales Tax Permit" required>
        <FileUploadBox label="Upload Sales Tax Permit" file={files.sales_tax_permit}
          onChange={f => setFiles(p => ({ ...p, sales_tax_permit: f }))} hint="State sales tax registration certificate" />
      </DocSection>

      <DocSection title="Certificate of Insurance" required>
        <Grid2>
          <Input label="Insurance Provider" value={c.insurance.provider}
            onChange={e => setC('insurance', 'provider', e.target.value)} />
          <Input label="Policy Expiration Date" type="date" value={c.insurance.expiry}
            onChange={e => setC('insurance', 'expiry', e.target.value)} />
        </Grid2>
        <FileUploadBox label="Upload Insurance Certificate" file={files.insurance_cert}
          onChange={f => setFiles(p => ({ ...p, insurance_cert: f }))} hint="General liability insurance required (min $1M)" />
      </DocSection>

      <SectionTitle>Optional Documents</SectionTitle>

      <DocSection title="Reseller Permit">
        <FileUploadBox label="Upload Reseller Permit" file={files.reseller_permit}
          onChange={f => setFiles(p => ({ ...p, reseller_permit: f }))} hint="If applicable for tax-exempt purchases" />
      </DocSection>

      <DocSection title="Fire Inspection Certificate">
        <FileUploadBox label="Upload Fire Inspection Certificate" file={files.fire_inspection}
          onChange={f => setFiles(p => ({ ...p, fire_inspection: f }))} />
      </DocSection>

      <DocSection title="Occupancy Permit">
        <FileUploadBox label="Upload Certificate of Occupancy" file={files.occupancy_permit}
          onChange={f => setFiles(p => ({ ...p, occupancy_permit: f }))} />
      </DocSection>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// STEP 5 — Banking & Payout
// ─────────────────────────────────────────────────────────────
const Step5 = ({ form, update, files, setFiles, errors }) => {
  const b = form.banking;
  const set = (f, v) => update('banking', f, v);
  return (
    <div>
      <SectionTitle>Bank Account Details</SectionTitle>
      <Input label="Account Holder Name" required value={b.account_holder}
        onChange={e => set('account_holder', e.target.value)}
        error={errors.account_holder} />
      <Grid2>
        <Input label="Bank Name" required value={b.bank_name}
          onChange={e => set('bank_name', e.target.value)}
          error={errors.bank_name} />
        <Input label="Routing Number" required value={b.routing_number}
          onChange={e => set('routing_number', e.target.value)}
          placeholder="9-digit ABA routing number"
          error={errors.routing_number} />
      </Grid2>
      <Input label="Account Number" required value={b.account_number}
        onChange={e => set('account_number', e.target.value)}
        type="password" placeholder="Enter account number"
        hint="Your account number is encrypted and never stored in plaintext"
        error={errors.account_number} />

      <SectionTitle>Payout Settings</SectionTitle>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {[
          { value: 'weekly', label: 'Weekly Payout', sub: 'Every Monday' },
          { value: 'biweekly', label: 'Bi-Weekly', sub: 'Every other Monday' },
          { value: 'monthly', label: 'Monthly Payout', sub: '1st of each month' },
        ].map(opt => (
          <div key={opt.value} onClick={() => set('payout_frequency', opt.value)}
            style={{
              flex: 1, padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
              border: `2px solid ${b.payout_frequency === opt.value ? '#E85D04' : 'var(--admin-border)'}`,
              background: b.payout_frequency === opt.value ? '#FFF7ED' : 'var(--admin-card)',
              transition: 'all 0.15s',
            }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: b.payout_frequency === opt.value ? '#E85D04' : 'inherit' }}>{opt.label}</div>
            <div style={{ fontSize: 12, color: 'var(--admin-text-muted)', marginTop: 4 }}>{opt.sub}</div>
          </div>
        ))}
      </div>

      <SectionTitle>Tax Information</SectionTitle>
      <FileUploadBox label="W-9 Form" file={files.w9}
        onChange={f => setFiles(p => ({ ...p, w9: f }))}
        hint="Required for all restaurant payouts. Download blank W-9 from IRS.gov." />
      <Select label="Tax Classification" value={b.tax_classification}
        onChange={e => set('tax_classification', e.target.value)}
        options={[
          { value: 'individual', label: 'Individual / Sole Proprietor' },
          { value: 'llc_single', label: 'LLC (Single Member)' },
          { value: 'llc_multi', label: 'LLC (Multi-Member)' },
          { value: 'corporation', label: 'C Corporation' },
          { value: 's_corporation', label: 'S Corporation' },
          { value: 'partnership', label: 'Partnership' },
        ]} />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// STEP 6 — Operating Hours
// ─────────────────────────────────────────────────────────────
const Step6 = ({ form, update }) => {
  const oh = form.operating_hours;
  const setDay = (day, field, val) => update('operating_hours', day, { ...oh[day], [field]: val });

  return (
    <div>
      <SectionTitle>Weekly Schedule</SectionTitle>
      <div style={{ display: 'grid', gap: 0 }}>
        {DAYS.map(day => (
          <div key={day} style={{
            display: 'grid', gridTemplateColumns: '130px 1fr 1fr 120px',
            alignItems: 'center', gap: 16, padding: '14px 0',
            borderBottom: '1px solid var(--admin-border)',
            opacity: oh[day]?.closed ? 0.45 : 1,
          }}>
            <div style={{ fontWeight: 700, fontSize: 14, textTransform: 'capitalize' }}>{day}</div>
            <div>
              <Label>Open</Label>
              <input type="time" value={oh[day]?.open || '09:00'}
                onChange={e => setDay(day, 'open', e.target.value)}
                disabled={oh[day]?.closed}
                style={{ padding: '8px 10px', border: '1px solid var(--admin-border)', borderRadius: 6, background: 'var(--admin-card)', color: 'var(--admin-text)', fontSize: 14, width: '100%' }} />
            </div>
            <div>
              <Label>Close</Label>
              <input type="time" value={oh[day]?.close || '21:00'}
                onChange={e => setDay(day, 'close', e.target.value)}
                disabled={oh[day]?.closed}
                style={{ padding: '8px 10px', border: '1px solid var(--admin-border)', borderRadius: 6, background: 'var(--admin-card)', color: 'var(--admin-text)', fontSize: 14, width: '100%' }} />
            </div>
            <div style={{ paddingTop: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={oh[day]?.closed || false}
                  onChange={e => setDay(day, 'closed', e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: '#EF4444' }} />
                <span style={{ color: oh[day]?.closed ? '#EF4444' : 'var(--admin-text-muted)', fontWeight: 600 }}>Closed</span>
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// STEP 7 — Order Slot Configuration
// ─────────────────────────────────────────────────────────────
const Step7 = ({ form, update }) => {
  const sc = form.slot_config;
  const oh = form.operating_hours;
  const set = (f, v) => update('slot_config', f, v);
  const [generating, setGenerating] = useState(false);

  const generateSlots = () => {
    setGenerating(true);
    // Find the most common open/close from Mon-Fri
    const weekdays = DAYS.slice(0, 5).filter(d => !oh[d]?.closed);
    const openTime  = weekdays[0] ? oh[weekdays[0]].open  : '09:00';
    const closeTime = weekdays[0] ? oh[weekdays[0]].close : '21:00';
    const duration  = sc.slot_duration || 60;
    const prep      = sc.prep_time || 20;

    const parseMin = s => { const [h, m] = s.split(':').map(Number); return h * 60 + m; };
    const openMin  = parseMin(openTime);
    const closeMin = parseMin(closeTime);

    const slots = [];
    let cur = openMin + prep;
    while (cur + duration <= closeMin) {
      const h = String(Math.floor(cur / 60)).padStart(2, '0');
      const m = String(cur % 60).padStart(2, '0');
      slots.push(`${h}:${m}`);
      cur += duration;
    }
    set('generated_slots', slots);
    setTimeout(() => setGenerating(false), 300);
  };

  const removeSlot = (idx) => set('generated_slots', (sc.generated_slots || []).filter((_, i) => i !== idx));
  const addCustomSlot = () => {
    const custom = sc.custom_slots || [];
    set('custom_slots', [...custom, '12:00']);
  };

  const allSlots = [...(sc.generated_slots || []), ...(sc.custom_slots || [])].sort();

  return (
    <div>
      <SectionTitle>Slot Configuration</SectionTitle>

      <div style={{ padding: '16px', background: 'var(--admin-bg)', borderRadius: 10, marginBottom: 20 }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>How slots work</div>
        <div style={{ fontSize: 13, color: 'var(--admin-text-muted)' }}>
          Customers choose a pickup time from the available slots. Each slot represents a window during which their order will be ready. The preparation time buffer prevents orders from being placed too close to opening.
        </div>
      </div>

      <Grid2>
        <div>
          <Label>Slot Duration (minutes)</Label>
          <select value={sc.slot_duration} onChange={e => set('slot_duration', Number(e.target.value))}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--admin-border)', borderRadius: 8, background: 'var(--admin-card)', color: 'var(--admin-text)', fontSize: 14, marginBottom: 18 }}>
            {[15, 30, 45, 60, 90, 120].map(d => <option key={d} value={d}>{d} minutes</option>)}
          </select>
        </div>
        <Input label="Preparation Buffer (minutes)" type="number" value={sc.prep_time}
          onChange={e => set('prep_time', Number(e.target.value))}
          hint="Time after opening before first slot appears" min="0" max="120" />
      </Grid2>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <button type="button" onClick={generateSlots}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 8, background: '#E85D04', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
          <RefreshCw size={16} style={{ animation: generating ? 'admin-spin 0.5s linear infinite' : 'none' }} />
          Auto-Generate Slots
        </button>
        <button type="button" onClick={addCustomSlot}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 8, background: 'transparent', color: '#E85D04', border: '2px solid #E85D04', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
          <Plus size={16} /> Add Custom Slot
        </button>
      </div>

      {allSlots.length > 0 && (
        <>
          <div style={{ fontWeight: 700, marginBottom: 10, fontSize: 13 }}>
            Generated Slots ({allSlots.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {(sc.generated_slots || []).map((slot, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, background: '#FFF7ED', border: '1px solid #FDBA74', fontSize: 13, fontWeight: 700, color: '#C2410C' }}>
                {slot}
                <X size={14} style={{ cursor: 'pointer', opacity: 0.6 }} onClick={() => removeSlot(i)} />
              </div>
            ))}
            {(sc.custom_slots || []).map((slot, i) => (
              <div key={`c${i}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="time" value={slot}
                  onChange={e => {
                    const custom = [...(sc.custom_slots || [])];
                    custom[i] = e.target.value;
                    set('custom_slots', custom);
                  }}
                  style={{ padding: '6px 10px', border: '2px dashed #E85D04', borderRadius: 8, background: '#FFF7ED', color: '#C2410C', fontSize: 13, fontWeight: 700 }} />
                <X size={14} style={{ cursor: 'pointer', color: '#EF4444' }}
                  onClick={() => set('custom_slots', (sc.custom_slots || []).filter((_, j) => j !== i))} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// STEP 8 — Kitchen Configuration
// ─────────────────────────────────────────────────────────────
const Step8 = ({ form, update }) => {
  const kc = form.kitchen_config;
  const set = (f, v) => update('kitchen_config', f, v);
  return (
    <div>
      <SectionTitle>Order Settings</SectionTitle>
      <Toggle label="Auto Accept Orders"
        description="Orders are accepted automatically without manual review"
        checked={kc.auto_accept} onChange={v => set('auto_accept', v)} />
      <div style={{ height: 1, background: 'transparent', marginBottom: 4 }} />
      <Toggle label="Manual Acceptance"
        description="Staff must review and accept each incoming order"
        checked={!kc.auto_accept} onChange={v => set('auto_accept', !v)} />

      <SectionTitle>Preparation Times</SectionTitle>
      <Grid2>
        <Input label="Default Prep Time (min)" type="number" value={kc.default_prep_time}
          onChange={e => set('default_prep_time', Number(e.target.value))}
          min="5" max="120" hint="Standard orders" />
        <Input label="Rush Hour Prep Time (min)" type="number" value={kc.rush_prep_time}
          onChange={e => set('rush_prep_time', Number(e.target.value))}
          min="5" max="180" hint="During peak hours" />
      </Grid2>

      <SectionTitle>Capacity Settings</SectionTitle>
      <Grid2>
        <Input label="Max Orders Per Hour" type="number" value={kc.max_orders_per_hour}
          onChange={e => set('max_orders_per_hour', Number(e.target.value))}
          min="1" max="500" hint="Orders accepted each hour before auto-pause" />
        <Input label="Max Orders Per Slot" type="number" value={kc.max_orders_per_slot}
          onChange={e => set('max_orders_per_slot', Number(e.target.value))}
          min="1" max="50" hint="Orders per pickup time slot" />
      </Grid2>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// STEP 9 — Delivery Configuration
// ─────────────────────────────────────────────────────────────
const Step9 = ({ form, update }) => {
  const dc = form.delivery_config;
  const set = (f, v) => update('delivery_config', f, v);
  return (
    <div>
      <SectionTitle>Service Modes</SectionTitle>
      <Toggle label="Delivery" description="Restaurant delivers orders to customer's address"
        checked={dc.delivery_enabled} onChange={v => set('delivery_enabled', v)} />
      <Toggle label="Pickup" description="Customers pick up orders from the restaurant"
        checked={dc.pickup_enabled} onChange={v => set('pickup_enabled', v)} />
      <Toggle label="Catering" description="Restaurant offers catering for events"
        checked={dc.catering_enabled} onChange={v => set('catering_enabled', v)} />

      {dc.delivery_enabled && (
        <>
          <SectionTitle>Delivery Settings</SectionTitle>
          <Grid2>
            <Input label="Minimum Order Amount ($)" type="number" value={dc.min_order_amount}
              onChange={e => set('min_order_amount', Number(e.target.value))}
              min="0" step="0.01" hint="Minimum cart value to place an order" />
            <Input label="Delivery Radius (miles)" type="number" value={dc.delivery_radius}
              onChange={e => set('delivery_radius', Number(e.target.value))}
              min="1" max="100" hint="Orders outside this radius are rejected" />
          </Grid2>
          <Grid2>
            <Input label="Estimated Delivery Time (min)" type="number" value={dc.estimated_delivery_time}
              onChange={e => set('estimated_delivery_time', Number(e.target.value))}
              min="15" max="180" hint="Shown to customers at checkout" />
            <Input label="Delivery Fee ($)" type="number" value={dc.delivery_fee}
              onChange={e => set('delivery_fee', Number(e.target.value))}
              min="0" step="0.01" />
          </Grid2>
          <Input label="Free Delivery Threshold ($)" type="number" value={dc.free_delivery_threshold}
            onChange={e => set('free_delivery_threshold', Number(e.target.value))}
            min="0" step="0.01" hint="Orders above this amount get free delivery (set 0 to disable)" style={{ maxWidth: 240 }} />
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// STEP 10 — Payment Configuration
// ─────────────────────────────────────────────────────────────
const Step10 = ({ form, update }) => {
  const pc = form.payment_config;
  const set = (f, v) => update('payment_config', f, v);
  return (
    <div>
      <SectionTitle>Accepted Payment Methods</SectionTitle>
      {[
        { key: 'credit_cards',  label: 'Credit Cards', desc: 'Visa, Mastercard, Amex, Discover' },
        { key: 'debit_cards',   label: 'Debit Cards',  desc: 'Visa Debit, Mastercard Debit' },
        { key: 'apple_pay',     label: 'Apple Pay',    desc: 'Touch ID / Face ID payments' },
        { key: 'google_pay',    label: 'Google Pay',   desc: 'Android & Chrome payments' },
        { key: 'cash_pickup',   label: 'Cash (Pickup Orders Only)', desc: 'Customers pay cash at pickup counter' },
      ].map(({ key, label, desc }) => (
        <Toggle key={key} label={label} description={desc}
          checked={pc[key]} onChange={v => set(key, v)} />
      ))}

      <SectionTitle>Payment Processor Mapping</SectionTitle>
      <Grid2>
        <Input label="Stripe Account ID" value={pc.stripe_account}
          onChange={e => set('stripe_account', e.target.value)}
          placeholder="acct_XXXXXXXXXXXXXX"
          hint="Restaurant's connected Stripe account" />
        <Input label="Platform Payment Account" value={pc.platform_account}
          onChange={e => set('platform_account', e.target.value)}
          placeholder="Internal account reference"
          hint="Used for commission tracking" />
      </Grid2>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// STEP 11 — Menu Configuration
// ─────────────────────────────────────────────────────────────
const Step11 = ({ form, update }) => {
  const mc = form.menu_config;
  const cats = mc.categories || [];
  const setAll = (newCats) => update('menu_config', 'categories', newCats);

  const addCat = () => setAll([...cats, { name: '', description: '' }]);
  const removeCat = (i) => setAll(cats.filter((_, j) => j !== i));
  const updateCat = (i, field, val) => {
    const next = [...cats];
    next[i] = { ...next[i], [field]: val };
    setAll(next);
  };

  return (
    <div>
      <SectionTitle>Menu Categories</SectionTitle>
      <div style={{ padding: '12px 16px', background: 'var(--admin-bg)', borderRadius: 8, marginBottom: 20, fontSize: 13, color: 'var(--admin-text-muted)' }}>
        💡 Create the category structure now. Menu items can be added later from the restaurant's Menu section.
      </div>
      {cats.map((cat, i) => (
        <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 12, padding: '14px 16px', border: '1px solid var(--admin-border)', borderRadius: 10, background: 'var(--admin-bg)' }}>
          <div style={{ flex: '0 0 36px', height: 36, borderRadius: 8, background: '#FFF7ED', border: '1px solid #FDBA74', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#C2410C', fontSize: 13, marginTop: 2 }}>
            {i + 1}
          </div>
          <div style={{ flex: 1 }}>
            <input value={cat.name} onChange={e => updateCat(i, 'name', e.target.value)}
              placeholder="Category name (e.g. Biryani, Appetizers)"
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--admin-border)', borderRadius: 6, background: 'var(--admin-card)', color: 'var(--admin-text)', fontSize: 14, marginBottom: 8, boxSizing: 'border-box' }} />
            <input value={cat.description} onChange={e => updateCat(i, 'description', e.target.value)}
              placeholder="Optional description"
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--admin-border)', borderRadius: 6, background: 'var(--admin-card)', color: 'var(--admin-text)', fontSize: 13, boxSizing: 'border-box' }} />
          </div>
          <button type="button" onClick={() => removeCat(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: '4px', marginTop: 2 }}>
            <Trash2 size={16} />
          </button>
        </div>
      ))}
      <button type="button" onClick={addCat}
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 8, background: 'transparent', color: '#E85D04', border: '2px dashed #E85D04', cursor: 'pointer', fontWeight: 700, fontSize: 14, width: '100%', justifyContent: 'center', marginTop: 4 }}>
        <Plus size={16} /> Add Category
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// STEP 12 — Staff Management
// ─────────────────────────────────────────────────────────────
const Step12 = ({ form, update }) => {
  const staff = form.staff || [];
  const setStaff = (s) => update('staff', 'dummy', s);  // handled specially

  const addStaff = () => {
    const next = [...staff, { full_name: '', username: '', email: '', phone: '', role: 'kitchen_manager', password: '' }];
    setStaff(next);
  };
  const removeStaff = (i) => setStaff(staff.filter((_, j) => j !== i));
  const updateS = (i, f, v) => {
    const next = [...staff];
    next[i] = { ...next[i], [f]: v };
    setStaff(next);
  };

  return (
    <div>
      <SectionTitle>Staff Accounts</SectionTitle>
      <div style={{ padding: '12px 16px', background: 'var(--admin-bg)', borderRadius: 8, marginBottom: 20, fontSize: 13, color: 'var(--admin-text-muted)' }}>
        💡 The restaurant owner account is created in Step 2. Add additional staff here — they can log in to the kitchen portal with their credentials.
      </div>
      {staff.map((s, i) => (
        <div key={i} style={{ border: '1px solid var(--admin-border)', borderRadius: 10, padding: '16px', marginBottom: 16, background: 'var(--admin-bg)', position: 'relative' }}>
          <div style={{ fontWeight: 700, marginBottom: 14, color: '#E85D04', fontSize: 13 }}>Staff Member #{i + 1}</div>
          <button type="button" onClick={() => removeStaff(i)}
            style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444' }}>
            <Trash2 size={16} />
          </button>
          <Grid2>
            <Input label="Full Name" value={s.full_name}
              onChange={e => updateS(i, 'full_name', e.target.value)}
              placeholder="Staff member's full name" />
            <Select label="Role" value={s.role}
              onChange={e => updateS(i, 'role', e.target.value)}
              options={STAFF_ROLES} />
          </Grid2>
          <Grid2>
            <Input label="Email" type="email" value={s.email}
              onChange={e => updateS(i, 'email', e.target.value)} />
            <Input label="Phone" type="tel" value={s.phone}
              onChange={e => updateS(i, 'phone', e.target.value)} />
          </Grid2>
          <Grid2>
            <Input label="Username" required value={s.username}
              onChange={e => updateS(i, 'username', e.target.value)}
              placeholder="Unique login username" />
            <Input label="Temporary Password" required type="password" value={s.password}
              onChange={e => updateS(i, 'password', e.target.value)} />
          </Grid2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
            {['orders','menu','reports','payments','staff'].map(p => {
              const perms = s.permissions || [];
              const has = perms.includes(p);
              return (
                <span key={p} onClick={() => {
                  const next = has ? perms.filter(x => x !== p) : [...perms, p];
                  updateS(i, 'permissions', next);
                }}
                  style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer', textTransform: 'capitalize', border: `1.5px solid ${has ? '#E85D04' : 'var(--admin-border)'}`, background: has ? '#FFF7ED' : 'transparent', color: has ? '#E85D04' : 'var(--admin-text-muted)' }}>
                  {p}
                </span>
              );
            })}
            <span style={{ fontSize: 12, color: 'var(--admin-text-muted)', alignSelf: 'center', marginLeft: 4 }}>Permissions</span>
          </div>
        </div>
      ))}
      <button type="button" onClick={addStaff}
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 8, background: 'transparent', color: '#E85D04', border: '2px dashed #E85D04', cursor: 'pointer', fontWeight: 700, fontSize: 14, width: '100%', justifyContent: 'center' }}>
        <Plus size={16} /> Add Staff Member
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// STEP 13 — Visibility & Publishing
// ─────────────────────────────────────────────────────────────
const Step13 = ({ form, update }) => {
  const vis = form.visibility;
  const set = (f, v) => update('visibility', f, v);
  const statusLabel = form.status_label;
  const setStatus = (v) => update('status_label', 'dummy', v);

  const STATUS_OPTS = [
    { value: 'draft',    label: 'Draft',              desc: 'Not visible. Save for later completion.', color: '#6B7280' },
    { value: 'pending',  label: 'Pending Verification', desc: 'Queued for admin document verification.', color: '#F59E0B' },
    { value: 'active',   label: 'Active — Go Live 🚀', desc: 'Immediately visible on customer app and website.', color: '#10B981' },
  ];

  return (
    <div>
      <SectionTitle>Visibility Settings</SectionTitle>
      <Toggle label="Visible on Customer App" description="Restaurant appears in the mobile app listings"
        checked={vis.visible_on_app} onChange={v => set('visible_on_app', v)} />
      <Toggle label="Visible on Website" description="Restaurant appears on the website ordering platform"
        checked={vis.visible_on_website} onChange={v => set('visible_on_website', v)} />
      <Toggle label="Featured Restaurant" description="Promoted in the Featured section on homepage"
        checked={vis.featured} onChange={v => set('featured', v)} />
      <Toggle label="Recommended Restaurant" description="Appears in the Recommended for You section"
        checked={vis.recommended} onChange={v => set('recommended', v)} />

      <SectionTitle>Publication Status</SectionTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {STATUS_OPTS.map(opt => (
          <div key={opt.value} onClick={() => setStatus(opt.value)}
            style={{
              padding: '16px 20px', borderRadius: 10, cursor: 'pointer',
              border: `2px solid ${statusLabel === opt.value ? opt.color : 'var(--admin-border)'}`,
              background: statusLabel === opt.value ? `${opt.color}12` : 'var(--admin-card)',
              display: 'flex', alignItems: 'center', gap: 16, transition: 'all 0.15s',
            }}>
            <div style={{ width: 20, height: 20, borderRadius: '50%', border: `3px solid ${opt.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {statusLabel === opt.value && <div style={{ width: 10, height: 10, borderRadius: '50%', background: opt.color }} />}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: statusLabel === opt.value ? opt.color : 'inherit' }}>{opt.label}</div>
              <div style={{ fontSize: 12, color: 'var(--admin-text-muted)', marginTop: 2 }}>{opt.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <SectionTitle>Summary Review</SectionTitle>
      <div style={{ padding: '16px', background: 'var(--admin-bg)', borderRadius: 10, fontSize: 13, lineHeight: 1.8 }}>
        <strong>Restaurant:</strong> {form.restaurant_info.restaurant_name || '—'}<br />
        <strong>Owner:</strong> {form.owner_info.first_name} {form.owner_info.last_name} ({form.owner_info.email})<br />
        <strong>Address:</strong> {form.address_info.street}, {form.address_info.city}, {form.address_info.state} {form.address_info.zip_code}<br />
        <strong>Cuisine:</strong> {(form.restaurant_info.cuisine_types || []).join(', ') || '—'}<br />
        <strong>Slots:</strong> {(form.slot_config.generated_slots || []).length + (form.slot_config.custom_slots || []).length} configured<br />
        <strong>Staff:</strong> Owner + {(form.staff || []).length} additional account(s)<br />
        <strong>Status:</strong> <span style={{ textTransform: 'capitalize', fontWeight: 700 }}>{statusLabel}</span>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN WIZARD COMPONENT
// ─────────────────────────────────────────────────────────────
const CreateRestaurant = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(initForm());
  const [files, setFiles]       = useState(initFiles());
  const [errors, setErrors]     = useState({});
  const [saving, setSaving]     = useState(false);
  const contentRef = useRef(null);

  // Generic update: update(section, field, value)
  // Special case: step 11 menu_config.categories & step 12 staff handled via nested updater
  const update = useCallback((section, field, value) => {
    setFormData(prev => {
      if (section === 'staff') {
        return { ...prev, staff: value };
      }
      if (section === 'status_label') {
        return { ...prev, status_label: value };
      }
      if (field === 'categories') {
        return { ...prev, [section]: { ...prev[section], categories: value } };
      }
      return { ...prev, [section]: { ...prev[section], [field]: value } };
    });
  }, []);

  // Scroll to top of content on step change
  const goTo = (n) => {
    setStep(n);
    setErrors({});
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };

  // ── Validation ───────────────────────────────────────────
  const validate = () => {
    const errs = {};
    const { restaurant_info: ri, owner_info: oi, address_info: ai, compliance: c, banking: b } = formData;

    if (step === 1) {
      if (!ri.restaurant_name.trim()) errs.restaurant_name = 'Restaurant name is required';
    }
    if (step === 2) {
      if (!oi.first_name.trim()) errs.first_name = 'Required';
      if (!oi.last_name.trim())  errs.last_name  = 'Required';
      if (!oi.email.trim())      errs.email      = 'Required';
      if (!oi.mobile.trim())     errs.mobile     = 'Required';
      if (!oi.username.trim())   errs.username   = 'Required';
      if (oi.password && oi.password.length < 8) errs.password = 'Min 8 characters';
      if (!oi.password.trim())   errs.password   = 'Required';
    }
    if (step === 3) {
      if (!ai.street.trim()) errs.street   = 'Required';
      if (!ai.city.trim())   errs.city     = 'Required';
      if (!ai.state)         errs.state    = 'Required';
      if (!ai.zip_code.trim()) errs.zip_code = 'Required';
    }
    if (step === 4) {
      if (!c.business_license.number) errs.business_license_number = 'Required';
      if (!c.business_license.expiry) errs.business_license_expiry = 'Required';
      if (!c.ein.number)              errs.ein_number              = 'Required';
      if (!c.food_service_permit.number) errs.food_permit_number   = 'Required';
      if (!c.food_service_permit.expiry) errs.food_permit_expiry   = 'Required';
    }
    if (step === 5) {
      if (!b.account_holder) errs.account_holder = 'Required';
      if (!b.bank_name)      errs.bank_name      = 'Required';
      if (!b.routing_number) errs.routing_number = 'Required';
      if (!b.account_number) errs.account_number = 'Required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => { if (validate()) goTo(step + 1); };
  const handleBack = () => goTo(step - 1);

  // ── Submit ────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);

    try {
      // 1. Create restaurant record
      const payload = { ...formData };
      const result  = await adminApi.createRestaurant(payload);
      const rid     = result?.id || result?.restaurant?.kitchen_id;

      if (!rid) throw new Error('No restaurant ID returned from server');

      // 2. Upload images in parallel
      const imageUploads = [];
      if (files.logo)   imageUploads.push(adminApi.uploadRestaurantImage(rid, 'logo',   files.logo));
      if (files.cover)  imageUploads.push(adminApi.uploadRestaurantImage(rid, 'cover',  files.cover));
      if (files.banner) imageUploads.push(adminApi.uploadRestaurantImage(rid, 'banner', files.banner));
      files.gallery.forEach(f => imageUploads.push(adminApi.uploadRestaurantImage(rid, 'gallery', f)));
      if (imageUploads.length) await Promise.allSettled(imageUploads);

      // 3. Upload compliance documents
      const docMap = {
        business_license: files.business_license,
        state_registration: files.state_registration,
        food_permit: files.food_permit,
        health_permit: files.health_permit,
        sales_tax_permit: files.sales_tax_permit,
        insurance_cert: files.insurance_cert,
        w9: files.w9,
        reseller_permit: files.reseller_permit,
        fire_inspection: files.fire_inspection,
        occupancy_permit: files.occupancy_permit,
      };
      const docUploads = Object.entries(docMap)
        .filter(([, f]) => f)
        .map(([key, f]) => adminApi.uploadRestaurantDocument(rid, key, f));
      if (docUploads.length) await Promise.allSettled(docUploads);

      toast.success(`✅ Restaurant "${formData.restaurant_info.restaurant_name}" created successfully!`);
      navigate(`/admin/restaurants/${rid}`);
    } catch (err) {
      toast.error(err.message || 'Failed to create restaurant');
    } finally {
      setSaving(false);
    }
  };

  const completedSteps = step - 1;
  const progress = Math.round((completedSteps / (STEPS.length - 1)) * 100);

  return (
    <div style={{ display: 'flex', gap: 0, minHeight: '100vh', background: 'var(--admin-bg)' }}>

      {/* ── Step Sidebar ─────────────────────────────────────── */}
      <div style={{
        width: 280, flexShrink: 0, background: 'var(--admin-card)',
        borderRight: '1px solid var(--admin-border)',
        display: 'flex', flexDirection: 'column',
        position: 'sticky', top: 0, height: '100vh', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 20px 0' }}>
          <button onClick={() => navigate('/admin/restaurants')}
            style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--admin-text-muted)', fontSize: 13, marginBottom: 20, padding: 0 }}>
            <ArrowLeft size={15} /> Back to Restaurants
          </button>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--admin-text-muted)', marginBottom: 4 }}>New Restaurant</div>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 16 }}>Onboarding Wizard</div>
          {/* Progress bar */}
          <div style={{ height: 4, background: 'var(--admin-border)', borderRadius: 2, marginBottom: 24 }}>
            <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg, #E85D04, #F48C06)', borderRadius: 2, transition: 'width 0.3s' }} />
          </div>
        </div>

        {/* Steps list */}
        <nav style={{ flex: 1, padding: '0 12px 24px' }}>
          {STEPS.map(s => {
            const done = step > s.id;
            const active = step === s.id;
            const Icon = s.icon;
            return (
              <div key={s.id}
                onClick={() => done && goTo(s.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                  borderRadius: 8, marginBottom: 2, cursor: done ? 'pointer' : 'default',
                  background: active ? '#FFF7ED' : 'transparent',
                  transition: 'background 0.15s',
                }}>
                {/* Step number / check */}
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: done ? '#10B981' : (active ? '#E85D04' : 'var(--admin-border)'),
                  color: done || active ? '#fff' : 'var(--admin-text-muted)',
                  fontSize: 12, fontWeight: 800,
                }}>
                  {done ? <Check size={13} /> : s.id}
                </div>
                <div style={{ overflow: 'hidden' }}>
                  <div style={{ fontSize: 13, fontWeight: active ? 800 : 600, color: active ? '#E85D04' : done ? 'var(--admin-text)' : 'var(--admin-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {s.title}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--admin-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {s.subtitle}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>
      </div>

      {/* ── Main Content ─────────────────────────────────────── */}
      <div ref={contentRef} style={{ flex: 1, overflowY: 'auto', padding: '32px 40px', maxHeight: '100vh' }}>

        {/* Step header */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #E85D04, #F48C06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {React.createElement(STEPS[step - 1].icon, { size: 20, color: '#fff' })}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#E85D04' }}>Step {step} of {STEPS.length}</div>
              <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>{STEPS[step - 1].title}</h2>
            </div>
          </div>
          <div style={{ fontSize: 14, color: 'var(--admin-text-muted)' }}>{STEPS[step - 1].subtitle}</div>
        </div>

        {/* Validation error banner */}
        {Object.keys(errors).length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, marginBottom: 20, color: '#DC2626', fontSize: 13 }}>
            <AlertCircle size={16} />
            Please fix the highlighted fields before continuing.
          </div>
        )}

        {/* Step content */}
        <div style={{ background: 'var(--admin-card)', borderRadius: 12, padding: '28px 28px 8px', border: '1px solid var(--admin-border)', marginBottom: 24 }}>
          {step === 1  && <Step1  form={formData} update={update} files={files} setFiles={setFiles} errors={errors} />}
          {step === 2  && <Step2  form={formData} update={update} errors={errors} />}
          {step === 3  && <Step3  form={formData} update={update} errors={errors} />}
          {step === 4  && <Step4  form={formData} update={update} files={files} setFiles={setFiles} errors={errors} />}
          {step === 5  && <Step5  form={formData} update={update} files={files} setFiles={setFiles} errors={errors} />}
          {step === 6  && <Step6  form={formData} update={update} />}
          {step === 7  && <Step7  form={formData} update={update} />}
          {step === 8  && <Step8  form={formData} update={update} />}
          {step === 9  && <Step9  form={formData} update={update} />}
          {step === 10 && <Step10 form={formData} update={update} />}
          {step === 11 && <Step11 form={formData} update={update} />}
          {step === 12 && <Step12 form={formData} update={update} />}
          {step === 13 && <Step13 form={formData} update={update} />}
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 40 }}>
          <button type="button" onClick={handleBack} disabled={step === 1}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '11px 22px',
              borderRadius: 8, border: '2px solid var(--admin-border)',
              background: 'transparent', color: step === 1 ? 'var(--admin-text-muted)' : 'var(--admin-text)',
              cursor: step === 1 ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 14,
            }}>
            <ArrowLeft size={16} /> Back
          </button>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--admin-text-muted)' }}>
              {step} / {STEPS.length}
            </span>

            {step < STEPS.length ? (
              <button type="button" onClick={handleNext}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 28px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #E85D04, #F48C06)', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 14, boxShadow: '0 4px 14px rgba(232,93,4,0.3)' }}>
                Continue <ArrowRight size={16} />
              </button>
            ) : (
              <button type="button" onClick={handleSubmit} disabled={saving}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 28px', borderRadius: 8, border: 'none', background: saving ? '#9CA3AF' : 'linear-gradient(135deg, #059669, #10B981)', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 14, boxShadow: saving ? 'none' : '0 4px 14px rgba(16,185,129,0.3)' }}>
                {saving ? (
                  <><div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'admin-spin 0.7s linear infinite' }} /> Creating Restaurant…</>
                ) : (
                  <><Check size={16} /> Create Restaurant</>
                )}
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default CreateRestaurant;
